import { useState } from "react";
import { SlidersHorizontal, ChevronDown, ChevronUp } from "lucide-react";
import { Input } from "../../../components/ui/input";

export const SMART_VIEWS = [
  { key: "", label: "All" },
  { key: "favorites", label: "Favorites" },
  { key: "recently_used", label: "Recently Used" },
  { key: "most_assigned", label: "Most Assigned" },
  { key: "audit_exercises", label: "Audit Exercises" },
  { key: "my_drafts", label: "My Drafts" },
  { key: "needs_review", label: "Needs Review" },
  { key: "client_specific", label: "Client-Specific" },
  { key: "team_exercises", label: "Team Exercises" },
];

const DIFFICULTIES = ["beginner", "intermediate", "advanced"];
const ASSET_ROLES = ["worksheet", "instructions", "coach_guide", "demo_video", "demo_audio", "slides", "transcript", "reference", "thumbnail", "preview"];
const RENDERER_KEYS = ["content_viewer", "guided_practice", "reflection", "checklist", "interactive_worksheet", "card_board", "structure_builder", "annotated_text", "upload_media"];
const STATUSES = ["draft", "review", "published", "retired", "archived"];

export const EMPTY_FILTERS = {
  content_type: "", keyword: "", code: "", focus_key: "", programme_key: "", individual_or_team: "",
  duration_min: "", duration_max: "", difficulty: "", language: "", renderer_key: "", audit_eligible: "",
  asset_role: "", statuses: "", scope: "",
};

/**
 * Master v5 Prompt 20 checkpoint 2 — the 8 smart-view tabs + every V5
 * search dimension not already on the primary bar (content_type,
 * keyword). `view` and `filters` are fully controlled by the parent so
 * switching a smart view or a filter never touches list scroll state.
 */
export function LibraryAdvancedFilters({ view, onViewChange, filters, onFiltersChange }) {
  const [open, setOpen] = useState(false);
  function set(field, value) { onFiltersChange({ ...filters, [field]: value }); }

  return (
    <div className="mb-3" data-testid="library-advanced-filters">
      <div className="flex flex-wrap gap-1.5 mb-2" data-testid="library-smart-view-tabs">
        {SMART_VIEWS.map((v) => (
          <button
            key={v.key || "all"}
            onClick={() => onViewChange(v.key)}
            className={`text-xs rounded-full px-2.5 py-1 border ${view === v.key ? "bg-terracotta text-white border-terracotta" : "border-ink/15 text-ink-soft hover:text-ink"}`}
            data-testid={`library-smart-view-${v.key || "all"}`}
          >
            {v.label}
          </button>
        ))}
      </div>

      <button onClick={() => setOpen((o) => !o)} className="text-xs text-ink-soft hover:text-ink flex items-center gap-1 mb-2" data-testid="library-more-filters-toggle">
        <SlidersHorizontal size={11} /> More filters {open ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
      </button>

      {open && (
        <div className="grid grid-cols-3 gap-2 max-w-2xl border border-ink/10 bg-ink/[0.02] rounded-lg p-3" data-testid="library-more-filters-panel">
          <Input placeholder="Code (e.g. EX-)" value={filters.code} onChange={(e) => set("code", e.target.value)} className="h-7 text-xs" data-testid="library-filter-code" />
          <Input placeholder="Focus" value={filters.focus_key} onChange={(e) => set("focus_key", e.target.value)} className="h-7 text-xs" data-testid="library-filter-focus" />
          <Input placeholder="Programme" value={filters.programme_key} onChange={(e) => set("programme_key", e.target.value)} className="h-7 text-xs" data-testid="library-filter-programme" />

          <select value={filters.individual_or_team} onChange={(e) => set("individual_or_team", e.target.value)} className="border border-ink/15 rounded-md text-xs px-1.5 py-1 bg-white" data-testid="library-filter-audience">
            <option value="">Individual/Team…</option>
            <option value="individual">Individual</option>
            <option value="team">Team</option>
          </select>
          <select value={filters.difficulty} onChange={(e) => set("difficulty", e.target.value)} className="border border-ink/15 rounded-md text-xs px-1.5 py-1 bg-white" data-testid="library-filter-difficulty">
            <option value="">Difficulty…</option>
            {DIFFICULTIES.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
          <Input placeholder="Language" value={filters.language} onChange={(e) => set("language", e.target.value)} className="h-7 text-xs" data-testid="library-filter-language" />

          <Input type="number" min="0" placeholder="Min duration" value={filters.duration_min} onChange={(e) => set("duration_min", e.target.value)} className="h-7 text-xs" data-testid="library-filter-duration-min" />
          <Input type="number" min="0" placeholder="Max duration" value={filters.duration_max} onChange={(e) => set("duration_max", e.target.value)} className="h-7 text-xs" data-testid="library-filter-duration-max" />
          <select value={filters.renderer_key} onChange={(e) => set("renderer_key", e.target.value)} className="border border-ink/15 rounded-md text-xs px-1.5 py-1 bg-white" data-testid="library-filter-renderer">
            <option value="">Renderer…</option>
            {RENDERER_KEYS.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>

          <select value={filters.asset_role} onChange={(e) => set("asset_role", e.target.value)} className="border border-ink/15 rounded-md text-xs px-1.5 py-1 bg-white" data-testid="library-filter-asset-role">
            <option value="">Has asset…</option>
            {ASSET_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <select value={filters.scope} onChange={(e) => set("scope", e.target.value)} className="border border-ink/15 rounded-md text-xs px-1.5 py-1 bg-white" data-testid="library-filter-scope">
            <option value="">Scope…</option>
            <option value="reusable">Reusable</option>
            <option value="client_specific">Client-specific</option>
          </select>
          <select value={filters.audit_eligible} onChange={(e) => set("audit_eligible", e.target.value)} className="border border-ink/15 rounded-md text-xs px-1.5 py-1 bg-white" data-testid="library-filter-audit-eligible">
            <option value="">Audit-eligible…</option>
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>

          <select value={filters.statuses} onChange={(e) => set("statuses", e.target.value)} className="border border-ink/15 rounded-md text-xs px-1.5 py-1 bg-white col-span-3" data-testid="library-filter-lifecycle">
            <option value="">Lifecycle status…</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      )}
    </div>
  );
}
